import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, Store, type StateContext } from '@ngxs/store';
import {
  ApplyFilters,
  CancelFilters,
  GetFilters,
  RemoveAllFilters,
  ResetStore,
  UpdateFilterSelection,
} from './filters-search.actions';
import { patch, updateItem } from '@ngxs/store/operators';
import { FiltersSearchService } from '../../service/filters-search/filters-search.service';
import { map, tap } from 'rxjs';
import type { FilterSearch } from '../../service/filters-search/models/filters-search.model';
import { RecordsSearchState } from '../records-search/records-search.state';
import { FacetsState } from 'src/app/state/facets/facets.state';
import type { SearchCriteriaFacets } from '../../service/search.model';
import { Router } from '@angular/router';

export interface FiltersSearchStateModel {
  isLoading: boolean;
  filters: FilterSearch[];
  filtersBackup: FilterSearch[];
}

export const initialState: FiltersSearchStateModel = {
  isLoading: false,
  filters: [],
  filtersBackup: [],
};

@State<FiltersSearchStateModel>({
  name: 'filtersSearch',
  defaults: initialState,
})
@Injectable()
export class FiltersSearchState {
  private readonly filtersSearchService = inject(FiltersSearchService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);

  @Selector()
  static isLoading(state: FiltersSearchStateModel) {
    return state.isLoading;
  }

  @Selector()
  static filters(state: FiltersSearchStateModel) {
    return state.filters;
  }

  @Selector()
  static filtersBackup(state: FiltersSearchStateModel) {
    return state.filtersBackup;
  }

  @Action(GetFilters)
  getFilters(context: StateContext<FiltersSearchStateModel>): GetFilters {
    context.setState(
      patch<FiltersSearchStateModel>({
        isLoading: true,
      })
    );
    const state = context.getState();

    const query = this.store.selectSnapshot(RecordsSearchState.query);
    const criteriaFacets: SearchCriteriaFacets | undefined = this.router.url.includes('/facets')
      ? this.store.selectSnapshot(FacetsState.criteriaFacets)
      : undefined;
    const filters: string = state.filtersBackup
      .reduce((acc, group) => {
        const selectedItem = group.filterItems.find((item) => item.selected);
        if (selectedItem) {
          acc.push(`${group.value}:"${selectedItem.value}"`);
        }
        return acc;
      }, [])
      .join(';');

    const currentBackupSelection = new Set(
      state.filtersBackup
        .flatMap((g) => g.filterItems)
        .filter((i) => i.selected)
        .map((i) => i.value)
    );

    return this.filtersSearchService
      .getFilters({
        query,
        filters,
        criteriaFacets,
      })
      .pipe(
        map((response) => {
          return response.map((group) => ({
            ...group,
            filterItems: group.filterItems.map((filter) => ({
              ...filter,
              selected: currentBackupSelection.has(filter.value),
            })),
          }));
        }),
        tap((newFilters) => {
          context.setState(
            patch<FiltersSearchStateModel>({
              filtersBackup: newFilters,
              isLoading: false,
            })
          );
        })
      );
  }

  @Action(UpdateFilterSelection)
  updateFilterSelection(
    context: StateContext<FiltersSearchStateModel>,
    { filterGroup, filterItem }: UpdateFilterSelection
  ): void {
    context.setState(
      patch<FiltersSearchStateModel>({
        filtersBackup: updateItem<FilterSearch>(
          (group) => group.value === filterGroup,
          patch<FilterSearch>({
            filterItems: (items) =>
              items.map((item) => ({
                ...item,
                selected: item.value === filterItem ? !item.selected : false,
              })),
          })
        ),
      })
    );

    context.dispatch(new GetFilters());
  }

  @Action(RemoveAllFilters)
  removeAllFilters(context: StateContext<FiltersSearchStateModel>): void {
    const state = context.getState();

    const hasSelectedFilters = state.filtersBackup.some((group) => group.filterItems.some((item) => item.selected));
    if (!hasSelectedFilters) {
      return;
    }

    context.setState(
      patch<FiltersSearchStateModel>({
        filtersBackup: state.filtersBackup.map((group) => ({
          ...group,
          filterItems: group.filterItems.map((item) => ({ ...item, selected: false })),
        })),
      })
    );

    context.dispatch(new GetFilters());
  }

  @Action(ApplyFilters)
  applyFilters(context: StateContext<FiltersSearchStateModel>): void {
    const state = context.getState();
    context.setState(
      patch<FiltersSearchStateModel>({
        filters: structuredClone(state.filtersBackup),
      })
    );
  }

  @Action(CancelFilters)
  cancelFilters(context: StateContext<FiltersSearchStateModel>) {
    const state = context.getState();
    context.setState(
      patch<FiltersSearchStateModel>({
        filtersBackup: structuredClone(state.filters),
      })
    );
  }

  @Action(ResetStore)
  resetStore(context: StateContext<FiltersSearchStateModel>) {
    context.setState(initialState);
  }
}
